/**
 * Job status utility functions
 * Pure functions for checking job status
 */
import {
  IN_PROGRESS_JOB_STATUS,
  FAILED_JOB_STATUS,
  SUCCESS_JOB_STATUS,
} from "../types/jobs.types";
import type { Job, PushedJob } from "../types/jobs.types";

/**
 * Check if a job is currently in progress (pending, starting, or in_progress)
 */
export const isInProgress = ({ status_code }: Job | PushedJob): boolean =>
  IN_PROGRESS_JOB_STATUS.includes(status_code);

/**
 * Check if a job has completed (either successfully or with failure)
 */
export const isComplete = ({ status_code }: Job | PushedJob): boolean =>
  [...SUCCESS_JOB_STATUS, ...FAILED_JOB_STATUS].includes(status_code);

/**
 * Check if a job has failed
 */
export const isFailed = ({ status_code }: Job | PushedJob): boolean =>
  FAILED_JOB_STATUS.includes(status_code);

/**
 * Check if a job completed successfully
 */
export const isSuccess = ({ status_code }: Job | PushedJob): boolean =>
  SUCCESS_JOB_STATUS.includes(status_code);

/**
 * Whether a job's gauge is moving (rising for an import, falling for a cancellation) and
 * therefore worth re-hydrating from the API on tab wake. Excludes "pending" (queued, no
 * progress yet) and terminal states.
 */
export const isGaugeMoving = ({ status_code }: Job | PushedJob): boolean =>
  status_code === "starting" || status_code === "in_progress" || status_code === "cancelling";

/**
 * Whether the job is being cancelled (reverse-progress in flight, not yet terminal).
 */
export const isCancelling = ({ status_code }: Job | PushedJob): boolean =>
  status_code === "cancelling";
